import useLoginUser from '@/modules/auth/hooks/useLoginUser';
import type { LoginFormValues } from '@/modules/auth/types/login-form-values';
import { BaseError } from '@/modules/errors/constants/error-class-map.gen';
import logger from '@/modules/logger/constants/logger';
import { useForm } from '@tanstack/react-form';
import { createFileRoute, useRouter } from '@tanstack/react-router';
import {
  Button,
  Input,
  Modal,
  Typography,
} from '@thisisdmg/tirecloud-patterns/v2';
import { IconLoadingOutlined } from '@thisisdmg/tirecloud-patterns/v2/components/icons';
import clsx from 'clsx';
import Joi from 'joi';
import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { match, P } from 'ts-pattern';
import classes from './index.module.css';
import { type } from 'arktype';
import { omit } from 'lodash-es';

function LoginPage() {
  const { t } = useTranslation();
  const router = useRouter();
  const action = Route.useSearch({ select: (s) => s.action });

  useEffect(() => {
    // preloading home and promotions since these are the first pages the user will see
    // after login
    try {
      router.preloadRoute({ to: '/' });
      router.preloadRoute({ to: '/promotions' });
    } catch (error) {
      logger.error('core.router', error);
    }
  }, [router]);

  const loginFormValidators = useMemo(() => {
    const username = Joi.string()
      .required()
      .custom((value, helpers) => {
        // check for email if value includes @
        if (typeof value === 'string' && value.includes('@')) {
          const { error } = Joi.string()
            .email({ tlds: { allow: false } })
            .validate(value);
          if (error) return helpers.error('string.email');
          return value;
        }
        const { error } = Joi.string().alphanum().min(8).validate(value);
        if (error) return helpers.error('string.alphanum');
        // eslint-disable-next-line @typescript-eslint/no-unsafe-return
        return value;
      })
      .messages({
        'string.empty': t('auth.errors.required_email_or_username'),
        'any.required': t('auth.errors.required_email_or_username'),
        'string.email': t('auth.errors.invalid_email'),
        'string.alphanum': t('auth.errors.invalid_username'),
      });

    const password = Joi.string()
      .required()
      .messages({
        'string.empty': t('auth.errors.required_password'),
        'any.required': t('auth.errors.required_password'),
      });

    const form = Joi.object({
      username,
      password,
    });

    return { form, username, password };
  }, [t]);

  const loginUser = useLoginUser();
  const navigate = Route.useNavigate();
  const redirect = Route.useSearch({ select: (s) => s.redirect });

  const form = useForm({
    defaultValues: {
      username: '',
      password: '',
    } as LoginFormValues,
    validators: {
      onSubmit: loginFormValidators.form,
    },
    onSubmit: async ({ value, formApi }) => {
      return loginUser({
        type: 'login',
        credentials: value,
      })
        .then(() => {
          let parsedRedirectUrl = new URL('/', window.location.origin);
          if (redirect !== undefined) {
            try {
              parsedRedirectUrl = new URL(
                redirect ?? '/',
                redirect.startsWith('http://') ||
                  redirect.startsWith('https://')
                  ? undefined
                  : window.location.origin
              );
            } catch (error) {
              logger.error('core.router', error);
            }
          }
          parsedRedirectUrl.searchParams.set('ref', 'auth_login');
          if (window.location.origin !== parsedRedirectUrl.origin) {
            navigate({
              href: parsedRedirectUrl.toString(),
            });
          } else {
            navigate({
              to: parsedRedirectUrl.pathname,
              search: Object.fromEntries(parsedRedirectUrl.searchParams),
              replace: true,
            });
          }
        })
        .catch((error) => {
          match(error)
            .with(P.instanceOf(BaseError), (e) => {
              formApi.fieldInfo.password.instance?.setErrorMap({
                onSubmit: {
                  message: t(`errors.gen.${e.path}`, { errorId: e.id }),
                },
              });
            })
            .with(
              P.when(
                (e) =>
                  e instanceof Error && e.message === 'REFRESHING_IN_PROGRESS'
              ),
              () => {}
            )
            .otherwise(() => {
              formApi.fieldInfo.password.instance?.setErrorMap({
                onSubmit: {
                  message: t('errors.gen.auth_login_general', { errorId: 1 }),
                },
              });
            });
        });
    },
  });

  return (
    <>
      <Modal
        open={action === 'force_logout'}
        closable={false}
        okButtonProps={{
          color: 'brand',
        }}
        cancelButtonProps={{
          hidden: true,
        }}
        onOk={() => {
          navigate({
            search: (s) => omit(s, 'reason', 'action'),
            replace: true,
          });
        }}
      >
        <Typography
          variant="heading-3"
          className={classes.session_expired_modal__title}
        >
          {t('auth.session_expired.title')}
        </Typography>
        <Typography variant="body">
          {t('auth.session_expired.description')}
        </Typography>
      </Modal>
      <div className={classes.login_form}>
        <Typography
          element="h1"
          variant="heading-2"
          className={classes.login_form__title}
        >
          {t('common.welcome')}
        </Typography>
        <Typography variant="body" className={classes.login_form__sub_title}>
          {t('auth.login.subtitle')}
        </Typography>
        <form
          className={classes.login_form__form}
          onSubmit={(e) => {
            e.preventDefault();
            form.handleSubmit();
          }}
        >
          <div className={classes.login_form__form_field}>
            <label htmlFor="login-form-username">
              {t('common.email_or_username')}
            </label>
            <form.Field
              name="username"
              validators={{
                onChange: loginFormValidators.username,
                onBlur: loginFormValidators.username,
              }}
              children={(field) => {
                return (
                  <Input
                    id="login-form-username"
                    placeholder={t('common.email_or_username')}
                    autoComplete="username"
                    name={field.name}
                    value={field.state.value}
                    onChange={(e) => field.handleChange(e.target.value)}
                    onBlur={field.handleBlur}
                    status={!field.state.meta.isValid ? 'error' : undefined}
                    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
                    errorMessage={field.state.meta.errors[0]?.message}
                  />
                );
              }}
            />
          </div>
          <div className={classes.login_form__form_field}>
            <label htmlFor="login-form-password">{t('common.password')}</label>
            <form.Field
              name="password"
              validators={{
                onChange: loginFormValidators.password,
                onBlur: loginFormValidators.password,
              }}
              children={(field) => {
                return (
                  <Input
                    id="login-form-password"
                    placeholder={t('common.password')}
                    autoComplete="current-password"
                    type="password"
                    name={field.name}
                    value={field.state.value}
                    onChange={(e) => field.handleChange(e.target.value)}
                    onBlur={field.handleBlur}
                    status={!field.state.meta.isValid ? 'error' : undefined}
                    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
                    errorMessage={field.state.meta.errors[0]?.message}
                  />
                );
              }}
            />
          </div>
          <div className={classes.login_form__form_field}>
            <form.Subscribe
              selector={(state) => [state.canSubmit, state.isSubmitting]}
              children={([canSubmit, isSubmitting]) => (
                <Button.Solid
                  color="brand"
                  type="submit"
                  disabled={!canSubmit}
                  icon={isSubmitting ? <IconLoadingOutlined spin /> : undefined}
                >
                  {t('common.login')}
                </Button.Solid>
              )}
            />
          </div>
        </form>
        <hr className={classes.login_form__separator} />
        <div className={classes.login_form__registration_info}>
          <Typography
            variant="heading-5"
            className={clsx(classes.login_form__registration__title)}
          >
            {t('auth.login.registration_info.title')}
          </Typography>
          <Typography
            variant="body"
            className={classes.login_form__registration__description}
          >
            {t('auth.login.registration_info.description')}
          </Typography>
        </div>
      </div>
    </>
  );
}

export const Route = createFileRoute('/_auth/auth/login')({
  component: LoginPage,
  validateSearch: type({
    'reason?': type("'unauthorized'")
      .or(type("'session_expired'"))
      .or(type("'refresh_failed'")),
  }),
});
